import { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

//Common
import {
  Button,
  ConfirmationDialog,
  DataGrid,
  Drawer,
  Snackbar,
} from "../common/VFX-UI";
import PageHeader from "../common/PageHeader/PageHeader";
import MoreActionsMenu from "../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../common/Buttons";
import SearchContainer from "../common/Forms/SearchContainer/SearchContainer";

//App
import { EmployeeFormContainer } from "./EmployeeFormContainer";
import useComponentGeneralStyles from "../styles";
import EmployeeView from "./EmployeeView";
import { getLeadershipEnrollmentStatusIcon, leadershipEnrollmentStatus } from "./constants";
import useEmployee from "./hooks/useEmployee";
import useEmployeeStyles from "./styles";
import { OrganizationEmployeeProps } from "../../types/organizationEmployee";
import { SearchProps } from "../../types";
import { SearchDefaultValue } from "../../store/types";
import { OrganizationEmployeeDto } from "../../app/model/api/types/dto";
import { SearchFieldsDefaultValue } from "../../utility/constants/constants";
import { EmployeeContext } from "./EmployeeContainer";
import DisplayData from "../common/DisplayData/DisplayData";
import { ColumnDescription } from "react-bootstrap-table-next";

const Employee: React.FC<OrganizationEmployeeProps> = (props) => {
  const {
    data: employees,
    activateMutation,
    deleteMutation,
    handleResendEmail,
    fullscreen,
  } = props;
  const generalStyles = useComponentGeneralStyles();

  const employeeStyles = useEmployeeStyles();

  const queryClient = useQueryClient();

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  const employeeContext = useContext(EmployeeContext);

  const hasManageEmployeesPermission =
    employeeContext?.hasManageEmployeesPermission;

  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleDeactivateHandler,
    toggleActivateHandler,
    handleDeactivate,
    handleActivate,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
    handleActiveToggleChange,
  } = useEmployee({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
    handleResendEmail: handleResendEmail,
  });

  const openMenu = Boolean(actionColumnAnchorEl);

  const searchProps: SearchProps<OrganizationEmployeeDto> = {
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };

  const handleOnSelectionChange = (selectedRows: any[]) => {
    setSelectedEmployeeIds(selectedRows.map((row) => row?.id));
  };

  const resendEmails = async () => {
    const response = await handleResendEmail(selectedEmployeeIds);
    if (response) {
      onShowSuccess("Email Sent", false);
    }
  };

  const invalidateEmployeeQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ["getEmployee", employeeContext?.id],
    });
  };

  var columns: ColumnDescription[] = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
  ];

  if (fullscreen === null || fullscreen === undefined || fullscreen) {
    columns = [
      ...columns,
      {
        dataField: "personnelNumber",
        text: "Personnel No.",
        sort: true,
      },
      {
        dataField: "firstName",
        text: "Name",
        formatter: (cell: any, row: any) => {
          return `${row.lastName}, ${row.firstName}`;
        },
        sort: true,
      },
      {
        dataField: "emailAddress",
        text: "Email Address",
        sort: true,
      },
      {
        dataField: "status",
        text: "Status"
      },
      {
        dataField: "donations",
        text: "Gottlieb Society",
        style: { width: "157px" },
        formatter: (cell: any, row: any) => {
          return <div style={{textAlign:"center"}}>
              {(row?.leadershipEnrollmentStatus !== leadershipEnrollmentStatus.none) && <i className={getLeadershipEnrollmentStatusIcon(row?.leadershipEnrollmentStatus)}  ></i> }  
            </div>
        }

      }
    ];
  } else {
    columns = [
      ...columns,
      {
        dataField: "firstName",
        text: "Name",
        formatter: (cell: any, row: any) => {
          return `${row.lastName}, ${row.firstName}`;
        },
        sort: true,
      },
      {
        dataField: "emailAddress",
        text: "Info",
        sort: false,
        formatter: (cell: boolean, row: any) => {
          //return <><b>Email: </b><span>{row.email}</span> <br /><b>Phone: </b><span>{row.phoneNumber}</span><br /></>
          return (
            <>
              <DisplayData
                text="Personnel No."
                importedData={row?.personnelNumber}
              />
              <DisplayData text="Email" importedData={row?.emailAddress} />
            </>
          );
        },
      },
    ];
  }

  columns = [
    ...columns,
    {
      dataField: "action",
      text: "",
      style: { width: "25px" },
      isDummyField: true,
      formatter: () => {
        return (
          <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
        );
      },
    },
  ];

  return (
    <>
      <PageHeader>Employees</PageHeader>
      <div style={generalStyles.searchContainer}>
        <SearchContainer
          searchProps={searchProps}
          activeToggle={formContext?.activeToggle ?? true}
          handleActiveToggleChange={handleActiveToggleChange}
          toggleNewViewHandler={
            hasManageEmployeesPermission ? toggleNewViewHandler : undefined
          }
        />
        {selectedEmployeeIds?.length > 0 && (
          <div
            style={employeeStyles.resendEmailButtonContainer}
            className="d-flex mb-3"
          >
            <Button
              onClick={resendEmails}
              style={employeeStyles.resendEmailButton}
            >
              Resend Email
            </Button>
          </div>
        )}
      </div>

      <Drawer show={state.toggleNewForm}>
        {state.toggleNewForm && (
          <EmployeeFormContainer
            onClose={toggleNewViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleEditForm}>
        {state.toggleEditForm && formContext?.data && (
          <EmployeeFormContainer
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleView}>
        {state.toggleView && formContext?.data && (
          <EmployeeView
            onClose={toggleViewHandler}
            editAction={() => {
              toggleEditViewHandler();
              invalidateEmployeeQuery();
            }}
            toggleDeactivateHandler={toggleDeactivateHandler}
            toggleActivateHandler={toggleActivateHandler}
          />
        )}
      </Drawer>

      <DataGrid
        columns={columns}
        data={employees}
        keyField="id"
        isRowSelectable={hasManageEmployeesPermission ? true : false}
        onSelectionChange={
          hasManageEmployeesPermission ? handleOnSelectionChange : undefined
        }
        rowEvents={rowEvents}
        totalSize={formContext?.paging?.totalSize}
        page={formContext?.paging?.currentPage}
        pageSize={formContext?.pageSize}
        remote={true}
        handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
          if (formContext?.setCurrentPage && newPage) {
            await formContext?.setCurrentPage(newPage);
          }
          if (formContext?.setCurrentPageSize && pageSize) {
            await formContext?.setCurrentPageSize(pageSize);
          }
          if (formContext?.setSortBy && sortField && sortOrder) {
            await formContext?.setSortBy({
              field: sortField,
              direction: sortOrder === "asc" ? 1 : -1,
            });
          }
          formContext?.refetchGrid();
        }}
      />

      {actionColumnAnchorEl && (
        <MoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            toggleViewHandler();
            setActionColumnAnchorEl(null);
            invalidateEmployeeQuery();
          }}
        />
      )}

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivate}
        contentTitle={`Deactivate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivate}
        contentTitle={`Activate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />

      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </>
  );
};

export default Employee;
